import { ChangeDetectionStrategy, Component, Input, OnInit, forwardRef, inject } from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { first } from 'rxjs';
import { CandidateService } from 'src/app/recruiting/candidates/candidate.service';
import { FeatureFlag } from '../../models/enums/feature-flag';
import { LDFeatureManager } from '../../services/ld-feature-manager';
import { ControlMasks } from '../../validators/control-masks';

@UntilDestroy()
@Component({
    selector: 'nova-npi-textbox',
    templateUrl: './npi-textbox.component.html',
    styleUrls: ['./npi-textbox.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NpiTextboxComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NpiTextboxComponent),
            multi: true
        },
        provideNgxMask()
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, MatIconModule, MatFormFieldModule, MatInputModule, NgxMaskDirective]
})
export class NpiTextboxComponent implements OnInit, ControlValueAccessor {
    @Input() requiredValidator: boolean;
    @Input() formGroup: FormGroup;
    @Input() isNpiValidated = false;

    private readonly ldFeatureManager = inject(LDFeatureManager);

    nationalProviderIdentifierMask = ControlMasks.nationalProviderNumber;
    npiFormControl = new FormControl();
    npi: string;
    savedNpiValue: string;
    resetNpiStatusOnNpiChangeEnabled = this.ldFeatureManager.isEnabledSignal(
        FeatureFlag.LocumsResetNPIStatusOnNPIChange
    );

    constructor(private readonly candidateService: CandidateService) {}

    ngOnInit(): void {
        this.npiFormControl.setValidators(this.getValidators());

        if (this.formGroup) {
            this.formGroup.addControl('npi', this.npiFormControl);
        }
    }

    onChange: any = () => {};
    onTouched: any = () => {};

    writeValue(value: any): void {
        this.savedNpiValue = value;
        this.npiFormControl.setValue(value);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    updateValue(event: Event): void {
        const newValue = (event.target as HTMLInputElement).value;
        this.npi = newValue;
        this.onChange(newValue);
        this.onTouched();
    }

    validate() {
        if (this.npiFormControl.invalid) {
            return { invalidNpi: true };
        }

        if (this.npiFormControl.value && this.npiFormControl.value !== this.npi) {
            this.npi = this.npiFormControl.value;

            this.candidateService
                .npiIsValid(this.npiFormControl.value)
                .pipe(first())
                .subscribe((x) => {
                    if (x) {
                        this.npiFormControl.updateValueAndValidity();
                    } else {
                        this.npiFormControl.setErrors({ invalidNpi: true });
                    }
                    this.npiFormControl.markAsTouched();
                });
        }

        return null;
    }

    private getValidators(): ValidatorFn[] {
        const validators: ValidatorFn[] = [Validators.pattern(/^[1-9]\d{9}$/), Validators.maxLength(10)];

        if (this.requiredValidator) {
            validators.push(Validators.required);
        }

        return validators;
    }
}
